// SubscriptionsTable — the Products section's default "Subscriptions" tab
// (account-area redesign Task 7, Amendment 2026-10-02 F6/F16 + ruling R4).
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 8). ORIGINAL: one row per owned PRODUCT's
// picked subscription, joined to the catalog, with a per-product store select
// and "Buy again" below it (O3). CURRENT: one row per SUBSCRIPTION ITEM served by
// GET /me/owned-products (R2: three subscriptions of one product are three
// rows). The store column is READ-ONLY (the subscription's own store, omitted
// without manage-stores), and "Buy again" is its own cell that adds with no
// store (F20). Everything below describes the unchanged cells.
//
// One EmbedTable row per subscription item. Per row:
//   - Product (primary): the thumbnail when the item carries image_url
//     (nothing at all otherwise — no placeholder box), the name, the type
//     badge.
//   - Store: the subscription's own store name, or the em dash (hidden
//     entirely without manage-stores).
//   - Renews: the next bill as a calendar date while ACTUALLY renewing
//     (status "active" AND no cancel_at); the em dash once a cancellation is
//     scheduled — a scheduled subscription must not advertise a renewal that
//     will not happen.
//   - Amount: recurring_amount as one price with the "/interval" label. The
//     OLD presentation's interval-only fallback (deriving "/monthly" from the
//     order line when no subscription existed) is RETIRED — this table only
//     ever renders rows that HAVE a subscription, so the amount always comes
//     from it (owned-products.ts's file header records the retirement).
//   - Status: the TD-389/TD-462 contract — "Active" badge while renewing;
//     once cancel_at is set, a warning "Cancels <service_end_date>" badge
//     (the SERVICE end, never the billing date) plus a helper line stating
//     the final charge and the resume deadline (or "No longer reversible"
//     past the deadline); status "canceled" renders the neutral "Ended"
//     badge and never the scheduled copy.
//   - Row links (stacked, ruling R4): the VISIBLE AA text links "Details" and
//     "Resume subscription" — never ⋯ menu items.
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 13, rulings R9/R13, finding F12).
// ORIGINAL: the row carried "Cancel renewal" while renewing and "Resume
// subscription" only while isReversibleCancellation(cancel_at) held, both
// decided client-side. CURRENT: the row has NO cancel control — cancel moved
// into the owned-item detail dialog (OwnedItemDetailDialog, opened by this
// row's "Details" button). "Resume subscription" stays on the row but shows
// only when the SERVER sent a `resume` action for the item; the "No longer
// reversible" helper means cancel_at is set and there is no resume action.
// A `help` action renders nothing (R13).
//   - Actions: the shared ProductRowActions ⋯ (hardware only — a hardware
//     product keeps its return affordance on this tab too).
//
// The resume CLICK handler lives in ProductsTab (network + state re-seed);
// this component renders state the tab hands over through resumeStateFor.
//
// This file also exports SubscriptionRowLink (the R4 link: textSecondary at
// rest — the old presentation used the disabled placeholder token, ~2.6:1,
// below AA — underline on hover AND keyboard focus) and the CancelState /
// ResumeState shapes, which ProductsTab imports type-only.

import {
  EmbedStatusBadge,
  EmbedTable,
  EmbedText,
} from "../../embed-shared/primitives";
import { formatCalendarDate } from "../../embed-shared/format";
import type { Subscription } from "../../embed-shared/types";
import { SubscriptionRowLink } from "./SubscriptionRowLink";
import { parseDollarAmount } from "./product-display";
import { PricingDisplay } from "./PricingDisplay";
import { BuyAgainCell, OwnedProductCell, ProductRowActions, type BuyAgainState } from "./OwnedProductsTable";
import type { OwnedItemWire, OwnedLineWire } from "./owned-products-api";
import type { EmbedTableColumn } from "../../embed-shared/primitives/EmbedTable";

export interface CancelState {
  /** The confirm dialog is open for this subscription. */
  confirming: boolean;
  /** The cancel POST is in flight (the dialog's footer pins; the row link
   *  disables). */
  loading: boolean;
  /** The server's verbatim error, rendered INSIDE the dialog. */
  error: string | null;
}

export interface ResumeState {
  loading: boolean;
  error: string | null;
}

/** A subscription item whose `subscription` is present (a member without
 *  manage-subscriptions gets `subscription: null` and no row). */
export interface SubscriptionsTableRow {
  item: OwnedItemWire;
  subscription: Subscription;
  /** Re-seeded from a mutation, actions not yet re-read: the row shows "Updating…"
   *  ("Active" + "Updating…" for a pending resume) and no Resume control. */
  pending?: boolean;
}

export interface SubscriptionsTableProps {
  rows: SubscriptionsTableRow[];
  buyAgainStateFor: (productId: string) => BuyAgainState | undefined;
  onBuyAgain: (productId: string) => void;
  rmaStatusFor: (productId: string) => string | null;
  onRequestReturn: (productId: string, trigger: HTMLElement, lines: OwnedLineWire[]) => void;
  /** Open the owned-item detail dialog (it carries the cancel link, R9). */
  onOpenDetails: (item: OwnedItemWire, trigger: HTMLElement) => void;
  resumeStateFor: (subscriptionId: number) => ResumeState | undefined;
  onResume: (subscriptionId: number) => void;
  /** TD-583: false when the member lacks manage-stores -- no Store column
   *  (Buy again stays). Default true. */
  storesPermitted?: boolean;
  /** TD-583: false when the member lacks manage-rma -- no return-action column.
   *  Default true. */
  returnsPermitted?: boolean;
}

// The R4 row link lives in SubscriptionRowLink.tsx (moved by Task 13 so the
// owned-product table can use it without an import cycle); re-exported here.
export { SubscriptionRowLink };

/** The scheduled-state helper line: the final charge + resume deadline when
 *  the last bill still lies ahead of cancel_at, the bare deadline while the
 *  window is open, and "No longer reversible" once it has passed (the
 *  TD-462 D7 gap — status stays "active" until the janitor terminates, but
 *  resuming would serve a period that will never be billed). "Open" is the
 *  server's call (R9): it is open exactly when the item carries a `resume`
 *  action; cancel_at set with no resume action is "No longer reversible". */
function scheduledHelper(sub: Subscription, canResume: boolean): string | null {
  if (!sub.cancel_at) return null;
  const billDay = sub.next_bill_date?.slice(0, 10);
  const cancelDay = sub.cancel_at.slice(0, 10);
  if (canResume && billDay && cancelDay && billDay < cancelDay) {
    return `Final charge ${formatCalendarDate(sub.next_bill_date)} · Resume by ${formatCalendarDate(sub.cancel_at)}`;
  }
  if (canResume) {
    return `Resume by ${formatCalendarDate(sub.cancel_at)}`;
  }
  return "No longer reversible";
}

const UPDATING_COPY = "Updating…";

export function SubscriptionsTable({
  rows,
  buyAgainStateFor,
  onBuyAgain,
  rmaStatusFor,
  onRequestReturn,
  onOpenDetails,
  resumeStateFor,
  onResume,
  storesPermitted = true,
  returnsPermitted = true,
}: SubscriptionsTableProps) {

  // Eligibility is the server's: a `resume` action for THIS subscription.
  const canResume = (r: SubscriptionsTableRow): boolean =>
    r.item.actions.some((a) => a.type === "resume" && a.subscription_id === r.subscription.id);

  const rowLinksCell = (r: SubscriptionsTableRow) => {
    const sub = r.subscription;
    const state = resumeStateFor(sub.id);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SubscriptionRowLink
          label="Details"
          ariaLabel={`Details for ${r.item.product.name}`}
          onClick={(trigger) => onOpenDetails(r.item, trigger)}
        />
        {!r.pending && canResume(r) && (
          <SubscriptionRowLink
            label="Resume subscription"
            loadingLabel="Resuming..."
            loading={state?.loading}
            onClick={() => onResume(sub.id)}
          />
        )}
        {state?.error && (
          <EmbedText tone="error" size="xs">
            {state.error}
          </EmbedText>
        )}
      </div>
    );
  };

  const statusCell = (r: SubscriptionsTableRow) => {
    const sub = r.subscription;
    if (sub.status === "canceled") {
      // TD-389: "canceled" is OVER — never the scheduled copy.
      return <EmbedStatusBadge domain="subscription" value="canceled" />;
    }
    if (sub.cancel_at) {
      // TD-462 D6/D8: the badge announces the SERVICE end (from
      // service_end_date, falling back to cancel_at only if the server
      // omitted it — never derived by adding days); cancel_at only ever
      // labels the resume deadline in the helper.
      // A pending row (re-seeded, actions not re-read yet) shows the neutral
      // "Updating…" -- never an action-derived "No longer reversible" guess.
      const helper = r.pending ? UPDATING_COPY : scheduledHelper(sub, canResume(r));
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <EmbedStatusBadge
            tone="warning"
            label={`Cancels ${formatCalendarDate(sub.service_end_date ?? sub.cancel_at)}`}
          />
          {helper && (
            <EmbedText tone="secondary" size="xs">
              {helper}
            </EmbedText>
          )}
        </div>
      );
    }
    if (r.pending) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <EmbedStatusBadge domain="subscription" value="active" />
          <EmbedText tone="secondary" size="xs">
            {UPDATING_COPY}
          </EmbedText>
        </div>
      );
    }
    return <EmbedStatusBadge domain="subscription" value="active" />;
  };

  return (
    <EmbedTable<SubscriptionsTableRow>
      caption="Your subscriptions"
      rows={rows}
      rowKey={(r) => String(r.subscription.id)}
      rowTestId={(r) => `subscription-row-${r.subscription.id}`}
      columns={[
        {
          id: "product",
          header: "Product",
          primary: true,
          cell: (r) => <OwnedProductCell product={r.item.product} serviceOptions={r.item.service_options} />,
        },
        ...(storesPermitted
          ? ([
              {
                id: "store",
                header: "Store",
                cell: (r: SubscriptionsTableRow) => (
                  <EmbedText tone="secondary">{r.item.store?.name ?? "—"}</EmbedText>
                ),
              },
            ] as EmbedTableColumn<SubscriptionsTableRow>[])
          : []),
        {
          id: "renews",
          header: "Renews",
          cell: (r) => {
            const renewing = r.subscription.status === "active" && !r.subscription.cancel_at;
            return (
              <EmbedText tone="secondary">
                {renewing ? formatCalendarDate(r.subscription.next_bill_date) : "—"}
              </EmbedText>
            );
          },
        },
        {
          id: "amount",
          header: "Amount",
          numeric: true,
          cell: (r) => {
            const amount = parseDollarAmount(r.subscription.recurring_amount);
            if (amount == null) {
              // Malformed amount degrades to the empty marker — never a
              // fabricated "$0.00".
              return <EmbedText tone="secondary">—</EmbedText>;
            }
            return (
              <PricingDisplay
                amount={amount}
                recurringLabel={`/${r.subscription.recurring_interval}`}
              />
            );
          },
        },
        {
          id: "status",
          header: "Status",
          cell: statusCell,
        },
        {
          id: "buyAgain",
          header: "",
          stackedLabel: false,
          cell: (r) => (
            <BuyAgainCell productId={r.subscription.product_id} buyAgainStateFor={buyAgainStateFor} onBuyAgain={onBuyAgain} />
          ),
        },
        {
          id: "rowLinks",
          header: "",
          stackedLabel: false,
          cell: rowLinksCell,
        },
        // TD-583: the ⋯ menu carries only "Request a return" -- no manage-rma, no column.
        ...(returnsPermitted
          ? ([
              {
                id: "actions",
                header: "",
                stackedLabel: false,
                cell: (r: SubscriptionsTableRow) => (
                  <ProductRowActions
                    productId={r.subscription.product_id}
                    productName={r.item.product.name}
                    productType={r.item.product.product_type}
                    lines={r.item.lines}
                    rmaStatus={rmaStatusFor(r.subscription.product_id)}
                    onRequestReturn={onRequestReturn}
                  />
                ),
              },
            ] as EmbedTableColumn<SubscriptionsTableRow>[])
          : []),
      ]}
    />
  );
}
